import { ApiResourceBaseClass as i } from "./baseApiCalls.js";
import { ChatbotContentTypeEnum as r } from "../types/chatbot.js";
const e = "chatbots";
class l extends i {
  /**
   * Retrieves all chatbots of the org.
   *
   * HTTP Verb: `GET`
   * Endpoint URL: `/chatbots`
   *
   * @returns {Promise<Array<ChatbotType>>}  A promise that resolves to the chatbots.
   */
  getChatbots = async () => {
    const { data: t } = await this.get(e);
    return t;
  };
  /**
   * Retrieves one chatbot of the org.
   *
   * HTTP Verb: `GET`
   * Endpoint URL: `/chatbots/${chatbotId}`
   *
   * @param {number} chatbotId - The ID of the chatbot to retrieve.
   * @returns {Promise<ChatbotType>} A promise that resolves to the chatbot.
   */
  getChatbot = async (t) => {
    const { data: a } = await this.get(`${e}/${t}`);
    return a;
  };
  /**
   * Creates one chatbot to the org.
   *
   * HTTP Verb: `POST`
   * Endpoint URL: `/chatbots`
   *
   * @returns {Promise<ChatbotType>} A promise that resolves to the chatbot.
   */
  createChatbot = async () => {
    const { data: t } = await this.post(e);
    return t;
  };
  /**
   * Duplicates one chatbot of the org.
   *
   * HTTP Verb: `POST`
   * Endpoint URL: `/chatbots`
   *
   * @param {number} chatbotId - The ID of the chatbot to duplicate.
   * @returns {Promise<ChatbotType>} A promise that resolves to the chatbot.
   */
  duplicateChatbot = async (t) => {
    const { data: a } = await this.post(e, {
      duplicate_chatbot_id: t
    });
    return a;
  };
  /**
   * Updates one chatbot of the org.
   *
   * HTTP Verb: `PATCH`
   * Endpoint URL: `/chatbots/${chatbotId}`
   *
   * @param {ChatbotPatchPayload} payload - The list of chatbot fields to update.
   * @param {number} chatbotId - The ID of the chatbot to update.
   * @returns {Promise<ChatbotType>} A promise that resolves to the chatbot.
   */
  patchChatbot = async (t, a) => {
    const { data: s } = await this.patch(`${e}/${a}`, t);
    return s;
  };
  /**
   * Deletes one chatbot of the org.
   *
   * HTTP Verb: `DELETE`
   * Endpoint URL: `/chatbots/${chatbotId}`
   *
   * @param {number} chatbotId - The ID of the chatbot to delete.
   * @returns {Promise<boolean>} A promise that resolves to a boolean.
   */
  deleteChatbot = async (t) => {
    const { data: a } = await this.delete(`${e}/${t}`);
    return a;
  };
  /**
   * Assigns one chatbot of the org to multiple businesses.
   *
   * HTTP Verb: `PATCH`
   * Endpoint URL: `/chatbots/${chatbotId}/business-selection`
   *
   * @param {BusinessModalFiltersType} businessSearchFilter - filters that with represent the businesses that will be linked to the chatbot.
   * @param {number} chatbotId - The ID of the chatbot.
   * @returns {Promise<ChatbotType>} A promise that resolves to a chatbot.
   */
  patchChatbotBusinessSelection = async (t, a) => {
    const s = {
      business_search_params: t
    }, { data: n } = await this.patch(
      `${e}/${a}/business-selection`,
      s
    );
    return n;
  };
  /**
   * Validates that the provided filters corresponds to businesses without chatbot assigned.
   *
   * HTTP Verb: `POST`
   * Endpoint URL: `/chatbots/${chatbotId}/validate-business-selection`
   *
   * @param {BusinessModalFiltersType} businessSearchFilter - filters that with represent the businesses that will be linked to the chatbot.
   * @param {number} chatbotId - The ID of the chatbot.
   * @returns {Promise<ChatbotBusinessValidationResponse>} A promise that resolves to a list of businesses that already have a chatbot.
   */
  validateBusinesses = async (t, a) => {
    const s = {
      business_search_params: a
    }, { data: n } = await this.post(
      `${e}/${t}/validate-business-selection`,
      s
    );
    return n;
  };
  /**
   * Updates the knowledge of a chatbot.
   *
   * HTTP Verb: `PATCH`
   * Endpoint URL: `/chatbots/${chatbotId}/knowledges/${knowledgeId}`
   *
   * @param {ChatbotKnowledgeType} knowledge - Knowledge to be updated.
   * @param {number} chatbotId - Id of the chatbot.
   * @returns {Promise<ChatbotKnowledgeType>} A promise that resolves to a chatbot knowledge.
   */
  patchChatbotKnowledge = async (t, a) => {
    const { data: s } = await this.patch(
      `${e}/${a}/knowledges/${t.id}`,
      {
        content: t.content,
        content_type: r.qa
      }
    );
    return s;
  };
  /**
   * Uploads chatbot knowledge from a file.
   *
   * HTTP Verb: `POST`
   * Endpoint URL: `/chatbots/${chatbotId}/knowledges/import-qa`
   *
   * @param {File} file - File that contains the knowledge.
   * @param {number} chatbotId - Id of the chatbot.
   * @returns {Promise<boolean>} A promise that resolves to a state of upload.
   */
  uploadChatbotKnowledgeQA = async (t, a) => {
    const s = `${e}/${a}/knowledges/import-qa`, n = { "Content-Type": "multipart/form-data" }, o = new FormData();
    o.append("file", t);
    const { data: c } = await this.post(s, o, n);
    return c;
  };
  /**
   * Sends a message to the chatbot preview.
   *
   * HTTP Verb: `POST`
   * Endpoint URL: `/chatbots/chat-preview`
   *
   * @param {ChatbotMessage[]} messages - List of messages of the preview.
   * @param {number} chatbotId - Id of the chatbot.
   * @param {string} businessId - (Optional) Id of business that will be used for the preview.
   * @param {string} language - (Optional) Language of the conversation.
   * @returns {Promise<ChatbotPreviewResponse>} A promise that resolves to a preview message.
   */
  postChatbotPreviewMessage = async (t, a, s, n) => {
    const { data: o } = await this.post(`${e}/chat-preview`, {
      messages: t,
      business_id: s,
      chatbot_id: a,
      language: n
    });
    return o;
  };
  /**
   * Activates the chatbot.
   *
   * HTTP Verb: `PATCH`
   * Endpoint URL: `/chatbots/${chatbotId}/activation`
   *
   * @param {boolean} enable - Flag to know the state of chatbot activation.
   * @param {number} chatbotId - Id of the chatbot.
   * @returns {Promise<ChatbotType>} A promise that resolves to a preview message.
   */
  patchChatbotActivation = async (t, a) => {
    const { data: s } = await this.patch(`${e}/${a}/activation`, {
      enable: t
    });
    return s;
  };
  /**
   * Retrieves the list of chatbot tone of voices.
   *
   * HTTP Verb: `GET`
   * Endpoint URL: `/chatbots/tone-of-voices`
   *
   * @returns {Promise<ToneOfVoice>} A promise that resolves to a list of tone of voices.
   */
  getToneOfVoices = async () => {
    const { data: t } = await this.get(`${e}/tone-of-voices`);
    return t;
  };
  /**
   * Retrieves the lead collection settings linked to the chatbot.
   *
   * HTTP Verb: `GET`
   * Endpoint URL: `/chatbots/${chatbotId}/lead_collection`
   *
   * @param {number} chatbotId - Id of the chatbot.
   * @returns {Promise<LeadCollection>} A promise that resolves to a lead collection settings.
   */
  getLeadCollection = async (t) => {
    const { data: a } = await this.get(
      `${e}/${t}/lead_collection`
    );
    return a;
  };
  /**
   * Creates a lead collection settings linked to the chatbot.
   *
   * HTTP Verb: `POST`
   * Endpoint URL: `/chatbots/${chatbotId}/lead_collection`
   *
   * @param {number} chatbotId - Id of the chatbot.
   * @param {LeadCollectionFormType} payload - List of fields of the lead collection.
   * @returns {Promise<LeadCollection>} A promise that resolves to a lead collection settings.
   */
  createLeadCollection = async (t, a) => {
    const { data: s } = await this.post(
      `${e}/${t}/lead_collection`,
      a
    );
    return s;
  };
  /**
   * Updates a lead collection settings linked to the chatbot.
   *
   * HTTP Verb: `PATCH`
   * Endpoint URL: `/chatbots/${chatbotId}/lead_collection/${leadCollectionId}`
   *
   * @param {number} chatbotId - Id of the chatbot.
   * @param {number} leadCollectionId - Id of the lead collection.
   * @param {LeadCollectionFormType} payload - List of fields of the lead collection.
   * @returns {Promise<LeadCollection>} A promise that resolves to a lead collection settings.
   */
  updateLeadCollection = async (t, a, s) => {
    const { data: n } = await this.patch(
      `${e}/${t}/lead_collection/${a}`,
      s
    );
    return n;
  };
}
export {
  e as CHATBOT_URI,
  l as Chatbot
};
